import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Sentinel value standing in for the "(Not set)" option inside a MultiSelect's
 * `selected` array. Re-exported from companyFilter as FIELD_NOT_SET — kept in
 * sync there; this local copy keeps the component dependency-free of the filter
 * module. Callers that want a "(Not set)" choice pass `includeNotSet` and treat
 * this token in `selected`/`onChange` like any other value.
 */
export const MULTISELECT_NOT_SET = '__none__';

// Matches the closed-control styling of Field's <Select> (border/padding/text),
// but it's a real <button> so it can host the count badge and toggle a panel.
const triggerCls =
  'flex w-full items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm hover:border-slate-300 focus:border-slate-400 focus:outline-none';

// Shared list styling so the absolute and portaled panels look identical.
const panelCls =
  'max-h-64 overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg';

// The nearest scrollable ancestor's viewport rect, or null. Used to keep the
// portaled panel inside (say) a modal body rather than measuring against the
// whole window — otherwise a control near the modal's bottom sees the large
// backdrop area below it as "room" and opens over the modal's footer.
function scrollableBoundary(el: HTMLElement): DOMRect | null {
  let node = el.parentElement;
  while (node) {
    const oy = getComputedStyle(node).overflowY;
    if (oy === 'auto' || oy === 'scroll') return node.getBoundingClientRect();
    node = node.parentElement;
  }
  return null;
}

/**
 * Accessible multi-select dropdown. A trigger button shows a label and, when
 * anything is selected, a count badge; clicking it opens a panel of checkboxes
 * (one per allowed option, plus an optional "(Not set)" row). Closes on
 * outside-click or Escape. Self-contained and dependency-free.
 *
 * `selected` is the controlled set of chosen values (a subset of `values`, plus
 * MULTISELECT_NOT_SET when `includeNotSet` and the not-set row is ticked).
 * Selecting nothing means "no filter" — the caller decides what that implies.
 *
 * Layout/positioning options (defaults preserve the original filter-bar look):
 * - `fullWidth` makes the control span its container instead of the fixed
 *   `sm:w-52` filter width — use it inside a form `Field`.
 * - `anchorToBody` renders the panel in a portal with fixed positioning anchored
 *   to the trigger, so it isn't clipped by an ancestor's `overflow` (e.g. a
 *   scrolling modal body). Default keeps the inline absolute panel.
 * - `ariaLabel` overrides the trigger's accessible name (defaults to the
 *   filter-style "Filter by {label}").
 */
export function MultiSelect({
  label,
  values,
  selected,
  onChange,
  includeNotSet = false,
  fullWidth = false,
  anchorToBody = false,
  ariaLabel,
  labelFor,
  className = '',
}: {
  label: string;
  /** Allowed options, in display order. */
  values: string[];
  /** Currently-checked values (subset of `values` + optional MULTISELECT_NOT_SET). */
  selected: string[];
  onChange: (next: string[]) => void;
  /** When true, render a "(Not set)" checkbox backed by MULTISELECT_NOT_SET. */
  includeNotSet?: boolean;
  /** Span the container (form use) instead of the fixed `sm:w-52` filter width. */
  fullWidth?: boolean;
  /** Render the panel in a body portal so it isn't clipped by a scrolling ancestor. */
  anchorToBody?: boolean;
  /** Override the trigger's accessible name (defaults to "Filter by {label}"). */
  ariaLabel?: string;
  /** Map an option value to its display text (e.g. id → name). Defaults to the value itself. */
  labelFor?: (value: string) => string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  // For the portaled panel: where to pin it (one of top/bottom is null) and how
  // tall it may grow before scrolling, so a long list never runs off-screen.
  const [pos, setPos] = useState<{
    left: number;
    width: number;
    maxHeight: number;
    top: number | null;
    bottom: number | null;
  } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const labelId = useId();

  // Anchor the fixed-position panel to the trigger. Opens downward by default,
  // but flips above when there's little room below and more room above; either
  // way its height is capped to the available space (it then scrolls inside).
  // Space is measured against the nearest scrollable ancestor (e.g. the modal
  // body) so the panel stays inside it instead of covering the modal's footer.
  function reposition() {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const MARGIN = 8; // keep this clear of the boundary edges
    const GAP = 4; // gap between trigger and panel
    const PANEL_MAX = 256; // matches the inline panel's max-h-64
    const boundary = scrollableBoundary(el);
    const topBound = Math.max(MARGIN, boundary ? boundary.top : MARGIN);
    const bottomBound = Math.min(
      window.innerHeight - MARGIN,
      boundary ? boundary.bottom : window.innerHeight - MARGIN,
    );
    const left = Math.max(MARGIN, Math.min(r.left, window.innerWidth - r.width - MARGIN));
    const spaceBelow = bottomBound - r.bottom - GAP;
    const spaceAbove = r.top - topBound - GAP;
    const openUp = spaceBelow < Math.min(PANEL_MAX, 200) && spaceAbove > spaceBelow;
    const maxHeight = Math.max(96, Math.min(PANEL_MAX, openUp ? spaceAbove : spaceBelow));
    if (openUp) {
      // Pin the panel's bottom just above the trigger so it grows upward.
      setPos({
        left,
        width: r.width,
        maxHeight,
        top: null,
        bottom: window.innerHeight - r.top + GAP,
      });
    } else {
      setPos({ left, width: r.width, maxHeight, top: r.bottom + GAP, bottom: null });
    }
  }

  function handleToggle() {
    if (!open && anchorToBody) reposition();
    setOpen((o) => !o);
  }

  // While open: close on outside pointerdown or Escape. In portal mode the panel
  // lives outside `rootRef`, so the outside check spans both refs, and the panel
  // re-anchors on scroll/resize.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    // Capture phase + stopPropagation so an open dropdown swallows Escape before
    // a surrounding modal's document-level handler closes the whole modal.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    let cleanupReposition = () => {};
    if (anchorToBody) {
      const onReposition = () => reposition();
      window.addEventListener('resize', onReposition);
      // Capture phase so scrolls inside any nested container also re-anchor.
      window.addEventListener('scroll', onReposition, true);
      cleanupReposition = () => {
        window.removeEventListener('resize', onReposition);
        window.removeEventListener('scroll', onReposition, true);
      };
    }
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
      cleanupReposition();
    };
  }, [open, anchorToBody]);

  const selectedSet = new Set(selected);
  const toggle = (value: string) => {
    const next = new Set(selectedSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    // Preserve a stable order: configured options first (in `values` order),
    // then the not-set sentinel last, so the URL/param order is deterministic.
    const ordered = values.filter((v) => next.has(v));
    if (next.has(MULTISELECT_NOT_SET)) ordered.push(MULTISELECT_NOT_SET);
    onChange(ordered);
  };

  const count = selected.length;

  const options: { value: string; text: string }[] = values.map((v) => ({
    value: v,
    text: labelFor ? labelFor(v) : v,
  }));
  if (includeNotSet) options.push({ value: MULTISELECT_NOT_SET, text: '(Not set)' });

  const listContent = (
    <>
      <span id={labelId} className="sr-only">
        {label}
      </span>
      {options.length === 0 ? (
        <p className="px-3 py-2 text-sm text-slate-400">No options</p>
      ) : (
        options.map((opt) => {
          const checked = selectedSet.has(opt.value);
          return (
            <label
              key={opt.value}
              role="option"
              aria-selected={checked}
              className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                checked={checked}
                onChange={() => toggle(opt.value)}
              />
              <span className="truncate">{opt.text}</span>
            </label>
          );
        })
      )}
    </>
  );

  const panel = !open ? null : anchorToBody ? (
    pos &&
    createPortal(
      <div
        ref={panelRef}
        role="listbox"
        aria-multiselectable="true"
        aria-labelledby={labelId}
        style={{
          position: 'fixed',
          left: pos.left,
          width: pos.width,
          maxHeight: pos.maxHeight,
          ...(pos.top != null ? { top: pos.top } : {}),
          ...(pos.bottom != null ? { bottom: pos.bottom } : {}),
        }}
        className={`z-50 ${panelCls}`}
      >
        {listContent}
      </div>,
      document.body,
    )
  ) : (
    <div
      role="listbox"
      aria-multiselectable="true"
      aria-labelledby={labelId}
      className={`absolute left-0 z-20 mt-1 w-full min-w-max ${panelCls}`}
    >
      {listContent}
    </div>
  );

  return (
    <div ref={rootRef} className={`relative ${fullWidth ? 'w-full' : 'sm:w-52'} ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        className={triggerCls}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel ?? `Filter by ${label}`}
        onClick={handleToggle}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate">{label}</span>
          {count > 0 && (
            <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-slate-900 px-1.5 text-xs font-medium text-white">
              {count}
            </span>
          )}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {panel}
    </div>
  );
}
